/**
 * App-level blocking-overlay constants.
 *
 * Layering values for the full-screen overlays mounted app-wide by
 * `AppOverlayHost` (`BudgetExceededOverlay` / `AccountSuspendedOverlay`) — the
 * web port of the `OverlayEntry`s Flutter inserts into the root `Overlay`
 * (`root_screen.dart:306-320`, `overlay_widget.dart:14-191`).
 *
 * These are stacking-order values, not sizes: nothing here scales with the
 * viewport, so they live with the other shared UI constants rather than in
 * `responsiveDesign.ts`.
 */

export const APP_OVERLAY_CONSTANTS = {
  /**
   * Stacking order of the app-level blocking overlays.
   *
   * They must cover *every* other app surface, because they represent states
   * in which the app is not usable (budget exhausted / account suspended).
   * The existing bands they have to clear: stacked sheets start at
   * `SHEET_CONSTANTS.baseZIndex` (100) and `VideoUploadProgressOverlay` sits at
   * 9999 (`VideoUploadProgressOverlay.tsx:48`), so the blocking overlays take
   * the next band up.
   */
  zIndex: 10000,

  /**
   * Route path prefixes on which the account-suspended overlay stands down.
   *
   * The overlay is terminal and un-dismissible, so its single "Contact us"
   * control cannot work by clearing `accountSuspendedVisible` — that unblocks
   * the whole app as soon as the user navigates back. Instead the flag stays
   * raised for the entire suspended session and `AppOverlayHost` suppresses the
   * overlay only while the user is on one of these paths.
   *
   * The set is the transitive closure of the escape hatch, not just its first
   * hop: `/support` because the Contact page's first section links straight
   * into it (`ContactSupportSection.tsx:26`), and `/legal` because a
   * privacy-policy support item renders an "Open Privacy Policy" control into
   * `/legal/privacy-policy` (`SupportItemPage.tsx:162-167`). Stopping short at
   * either hop would slam the terminal overlay back over the very flow the
   * button opens. All three route families are entries in `PUBLIC_ROUTES`
   * (`routing/routes.config.tsx:80-150`: `legal`, `legal/*`, `contact`,
   * `support`, `support/:id`) — reachable signed-out — so exempting them grants
   * a suspended user nothing a signed-out visitor does not already have.
   */
  accountSuspendedExemptPaths: ['/contact', '/support', '/legal'] as const,
} as const;

export type AppOverlayConstants = typeof APP_OVERLAY_CONSTANTS;
